import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from 'contexts/AuthProvider';
import { ORDER_STAGE_TYPES, VISIBILITY_INDEX_TYPE } from 'helpers/enums';

const VISIBILITY_SCREENS = {
  [VISIBILITY_INDEX_TYPE.ORDER_VIEW]: 'item_view', // customizing a regular menu item
  [VISIBILITY_INDEX_TYPE.SPECIAL_ORDER_VIEW]: 'item_view_special', // customizing an item within a special
  [VISIBILITY_INDEX_TYPE.SPECIAL_VIEW]: 'special_selection', // choosing which items form the special
  [VISIBILITY_INDEX_TYPE.HALF_HALF_VIEW]: 'half_selection', // choosing the two halves
  [VISIBILITY_INDEX_TYPE.HALF_ORDER_VIEW]: 'item_detail_half', // customizing one half's toppings
  [VISIBILITY_INDEX_TYPE.VOUCHER_VIEW]: 'voucher_selection', // choosing which items redeem the voucher
  [VISIBILITY_INDEX_TYPE.VOUCHER_ORDER_VIEW]: 'item_detail_voucher', // customizing an item redeemed via voucher
};

const AUTH_SCREENS = {
  [ORDER_STAGE_TYPES.OAUTH_FORM]: 'login',
  [ORDER_STAGE_TYPES.NAME_FORM]: 'login_name',
  [ORDER_STAGE_TYPES.MOBILE_FORM]: 'login_phone',
  [ORDER_STAGE_TYPES.VERIFY_FROM]: 'login_otp',
};

const STAGE_SCREENS = {
  [ORDER_STAGE_TYPES.REVIEW]: 'order_review',
  [ORDER_STAGE_TYPES.PAYMENT]: 'order_payment',
  [ORDER_STAGE_TYPES.PAYMENT_CARD]: 'order_payment',
};

// Follows the same priority order as getMainComponents() in
// containers/Order/index.js, so the tracked screen always matches what the
// sidebar actually renders.
function getScreenName({
  bookingOpen,
  aboutOpen,
  loyaltyOpen,
  profileOpen,
  visibilityIndex,
  authOrderStage,
  stage,
  hasItems,
}) {
  if (bookingOpen) return 'booking';
  if (aboutOpen) return 'about';
  if (loyaltyOpen) return 'loyalty';
  if (profileOpen) return 'profile';
  if (visibilityIndex && VISIBILITY_SCREENS[visibilityIndex]) {
    return VISIBILITY_SCREENS[visibilityIndex];
  }
  if (AUTH_SCREENS[authOrderStage]) return AUTH_SCREENS[authOrderStage];
  if (stage >= ORDER_STAGE_TYPES.ALL_DONE) return 'home';
  if (STAGE_SCREENS[stage]) return STAGE_SCREENS[stage];
  return hasItems ? 'cart' : 'home';
}

// Clamp a single screen's dwell time so a tab left open (idle, or overnight)
// can't blow out the average engagement time metric.
const MAX_ENGAGEMENT_MS = 30 * 60 * 1000;

// Don't emit a flush-triggered engagement event for dwell times below this;
// it would just be noise (e.g. pagehide firing right after a hide-flush).
const MIN_FLUSH_MS = 1000;

// Each analytics channel's snippet is only injected by head-template.xml when
// its ID is configured for the store, and the template exposes the configured
// IDs on `window` (see `window.__gtmId` / `__ga4Id` / `__pixelId`). We gate on
// those so we never push events into a channel that isn't actually set up.
const gtmEnabled = () => typeof window !== 'undefined' && !!window.__gtmId;
const ga4Enabled = () =>
  typeof window !== 'undefined' &&
  !!window.__ga4Id &&
  typeof window.gtag === 'function';
const pixelEnabled = () =>
  typeof window !== 'undefined' &&
  !!window.__pixelId &&
  typeof window.fbq === 'function';

function emitScreenView(screenName) {
  if (typeof window === 'undefined') return;

  // GTM — use a Custom Event trigger on `virtual_screen_view` and a Data Layer
  // Variable for `screen_name`. `engagement_time_msec: 1` marks the session
  // as engaged from the very first screen; the real per-screen dwell time is
  // reported separately via `screen_engagement` (see emitScreenEngagement).
  if (gtmEnabled()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtual_screen_view',
      screen_name: screenName,
      engagement_time_msec: 1,
    });
  }

  // GA4 direct (gtag.js)
  if (ga4Enabled()) {
    window.gtag('event', 'virtual_screen_view', {
      screen_name: screenName,
      engagement_time_msec: 1,
    });
  }

  // Meta Pixel direct
  if (pixelEnabled()) {
    window.fbq('trackCustom', 'VirtualScreenView', {
      screen_name: screenName,
    });
  }
}

// Reports the visibility-aware time actually spent on `screenName`, fired
// when the user leaves it (screen change, tab hidden, or page unload). Keeping
// this separate from `virtual_screen_view` means dwell time is attributed to
// the screen it was spent on, not the screen being entered next. GA4 sums
// `engagement_time_msec` across all events, so session-level engagement
// metrics also pick this up. GTM needs a Custom Event trigger for
// `screen_engagement` if it should forward this too.
function emitScreenEngagement(screenName, engagementTimeMsec) {
  if (typeof window === 'undefined') return;

  if (gtmEnabled()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'screen_engagement',
      screen_name: screenName,
      engagement_time_msec: engagementTimeMsec,
    });
  }

  if (ga4Enabled()) {
    window.gtag('event', 'screen_engagement', {
      screen_name: screenName,
      engagement_time_msec: engagementTimeMsec,
    });
  }
}

/**
 * Analytics-only screen tracking for the sidebar SPA flow. Derives a logical
 * screen name from app state and, on every screen change, fires:
 *  - `virtual_screen_view` for the screen being entered (GTM, GA4, Meta Pixel)
 *  - `screen_engagement` for the screen being left, carrying its real
 *    (visibility-aware) dwell time as `engagement_time_msec`, correctly
 *    attributed to that screen's `screen_name` (GTM, GA4)
 * Engagement is also flushed when the tab is hidden or the page unloads, so
 * the final screen of a session still reports. Never touches the URL.
 */
export default function useScreenViewTracking({ rightSideViewVisibilityIndex }) {
  const { bookingOpen, aboutOpen, loyaltyOpen, profileOpen, currentOrder } =
    useSelector((state) => state.homeReducer);
  const { authOrderStage } = useAuth();

  const stage = currentOrder?.stage ?? ORDER_STAGE_TYPES.NONE;
  const hasItems = (currentOrder?.menuItems?.length ?? 0) > 0;

  const lastScreen = useRef(null);
  const screenEnteredAt = useRef(null); // Date.now() when lastScreen became active
  const hiddenAccumulated = useRef(0); // ms the tab has been hidden since screenEnteredAt
  const hiddenSince = useRef(null); // Date.now() the tab was last hidden, or null if visible

  // Returns the visibility-aware dwell time (ms) since the current screen
  // became active, and resets the measurement baseline to now, so the same
  // stretch of time can never be reported twice (transition after a flush
  // only reports the remainder).
  const takeDwellMs = useCallback(() => {
    if (screenEnteredAt.current === null) return null;
    const now = Date.now();
    let hiddenMs = hiddenAccumulated.current;
    if (hiddenSince.current !== null) hiddenMs += now - hiddenSince.current;
    const dwellMs = now - screenEnteredAt.current - hiddenMs;
    screenEnteredAt.current = now;
    hiddenAccumulated.current = 0;
    return Math.min(Math.max(dwellMs, 0), MAX_ENGAGEMENT_MS);
  }, []);

  // Track hidden-tab time (excluded from dwell, mirroring GA4's own
  // auto-tracking) and flush the current screen's engagement when the user
  // hides the tab or leaves the page — otherwise the last screen of a
  // session would never report its dwell time.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    if (document.visibilityState === 'hidden') hiddenSince.current = Date.now();

    const flushEngagement = () => {
      if (lastScreen.current === null) return;
      const dwellMs = takeDwellMs();
      if (dwellMs === null || dwellMs < MIN_FLUSH_MS) return;
      emitScreenEngagement(lastScreen.current, dwellMs);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Flush before starting the hidden clock so the visible stretch that
        // just ended is captured even if the tab never comes back.
        flushEngagement();
        hiddenSince.current = Date.now();
      } else if (hiddenSince.current !== null) {
        hiddenAccumulated.current += Date.now() - hiddenSince.current;
        hiddenSince.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushEngagement);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushEngagement);
    };
  }, [takeDwellMs]);

  useEffect(() => {
    const screenName = getScreenName({
      bookingOpen,
      aboutOpen,
      loyaltyOpen,
      profileOpen,
      visibilityIndex: rightSideViewVisibilityIndex,
      authOrderStage,
      stage,
      hasItems,
    });

    if (screenName === lastScreen.current) return; // no real change, skip

    // Report the time spent on the screen we're leaving, attributed to that
    // screen's name.
    if (lastScreen.current !== null) {
      const dwellMs = takeDwellMs();
      if (dwellMs !== null && dwellMs > 0) {
        emitScreenEngagement(lastScreen.current, dwellMs);
      }
    }

    lastScreen.current = screenName;
    screenEnteredAt.current = Date.now();
    hiddenAccumulated.current = 0;

    emitScreenView(screenName);
  }, [
    takeDwellMs,
    bookingOpen,
    aboutOpen,
    loyaltyOpen,
    profileOpen,
    rightSideViewVisibilityIndex,
    authOrderStage,
    stage,
    hasItems,
  ]);
}
